/**
 * Song coverage under the current commercial licence.
 *
 * A simple label/value list rather than a data grid: the owner needs to confirm which songs
 * are covered, which are uploads, and why. The component stays purely presentational; the
 * route decides what the current licence and song list are.
 */
import { useEffect, useRef, useState } from 'react';
import { Music4, ShieldCheck } from 'lucide-react';
import { c } from '../../styles/theme';
import type { LicensedSong } from '../../types/domain';

export interface LicensedSongsTableProps {
  songs: LicensedSong[];
}

function CoverageBadge({ covered }: { covered: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10.5,
        fontWeight: 600,
        color: covered ? c.success : c.text2,
        background: covered ? c.successSoft : c.surface2,
        padding: '3px 8px',
        borderRadius: 999,
      }}
    >
      <ShieldCheck size={11} />
      {covered ? 'Covered' : 'Not covered'}
    </span>
  );
}

export default function LicensedSongsTable({ songs }: LicensedSongsTableProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    function updateScrollable() {
      const node = listRef.current;
      if (!node) return;
      setIsScrollable(node.scrollHeight > node.clientHeight + 1);
    }

    updateScrollable();

    const observer = new ResizeObserver(updateScrollable);
    if (listRef.current) observer.observe(listRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    window.addEventListener('resize', updateScrollable);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScrollable);
    };
  }, [songs]);

  return (
    <>
      <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>Song coverage</p>
      <div
        ref={listRef}
        style={{
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          background: c.surface1,
          marginBottom: 18,
          maxHeight: '50vh',
          overflowY: isScrollable ? 'auto' : 'visible',
          overflowX: 'hidden',
          overscrollBehaviorY: isScrollable ? 'contain' : 'auto',
        }}
      >
        <div ref={contentRef}>
          {songs.length === 0 ? (
            <div style={{ padding: '14px 16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: 12, color: c.text1 }}>No songs to show yet.</p>
              <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                Songs tied to your account will appear here with their current commercial-license
                coverage status.
              </p>
            </div>
          ) : (
            songs.map((song, index) => (
              <div
                key={song.songId}
                style={{
                  padding: '12px 14px',
                  borderBottom: index === songs.length - 1 ? undefined : `1px solid ${c.border}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <Music4 size={14} color={c.accent} style={{ flexShrink: 0 }} />
                    <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 500 }}>
                      {song.title}
                    </p>
                  </div>
                  <p style={{ margin: '0 0 4px', fontSize: 11, color: c.text2 }}>
                    Generated {song.generatedAt}
                    {song.agreementVersionAtGeneration
                      ? ` · Agreement v${song.agreementVersionAtGeneration}`
                      : ''}
                  </p>
                  <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
                    {song.coverageReason}
                  </p>
                </div>
                <CoverageBadge covered={song.covered} />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
